import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';

function StatePanel({
  icon,
  spinning,
  title,
  description,
  children,
}: {
  icon: string;
  spinning?: boolean;
  title: string;
  description: string;
  children?: ReactNode;
}) {
  return (
    <section className="mx-auto w-full max-w-[1200px] px-4 py-20 md:px-8 md:py-28">
      <div className="mx-auto flex max-w-xl flex-col items-center rounded-lg border border-slate-200 bg-white px-6 py-14 text-center">
        <span className="flex h-16 w-16 items-center justify-center rounded-full bg-slate-50 text-slate-400 ring-1 ring-slate-200">
          <i className={`${icon} text-3xl ${spinning ? 'animate-spin' : ''}`} />
        </span>
        <h1 className="mt-5 text-lg font-bold text-slate-900 md:text-xl">{title}</h1>
        <p className="mt-2 text-sm leading-relaxed text-slate-500">{description}</p>
        {children && <div className="mt-6 flex flex-col gap-3 sm:flex-row">{children}</div>}
      </div>
    </section>
  );
}

export function WaitingState() {
  return (
    <StatePanel
      icon="ri-loader-4-line"
      spinning
      title="正在讀取健診結果"
      description="請稍候，系統正在彙整公開資料與同業比較結果。"
    />
  );
}

export function UnavailableState({ auditId }: { auditId?: string }) {
  return (
    <StatePanel
      icon="ri-file-search-line"
      title="尚未取得健診結果"
      description="目前沒有可顯示的健診資料。待後端資料來源串接完成後，此頁將自動載入對應的健診報告。"
    >
      <Link
        to="/gmb-audit"
        className="inline-flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md bg-teal-700 px-5 py-3 text-sm font-semibold text-white transition-colors hover:bg-teal-800"
      >
        <i className="ri-refresh-line" />
        重新健診
      </Link>
    </StatePanel>
  );
}

export function ReportErrorState({ variant }: { variant: 'api-error' | 'quota-error' }) {
  const isQuota = variant === 'quota-error';
  return (
    <StatePanel
      icon={isQuota ? 'ri-hourglass-line' : 'ri-plug-line'}
      title={isQuota ? '查詢額度已達上限' : '資料來源暫時無法連線'}
      description={
        isQuota
          ? '目前查詢服務的用量已達上限，請於稍後時段再試一次。'
          : '讀取健診結果時發生錯誤，請稍後重新嘗試。'
      }
    >
      <Link
        to="/gmb-audit"
        className="inline-flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md bg-teal-700 px-5 py-3 text-sm font-semibold text-white transition-colors hover:bg-teal-800"
      >
        <i className="ri-refresh-line" />
        重新健診
      </Link>
    </StatePanel>
  );
}